import { acceptHMRUpdate, defineStore } from 'pinia'
import AuthService from '../services/auth.service'
import SocialLoginService from '../services/socialLogin.service'
import { useRoute, useRouter } from 'vue-router'
import { PUBLIC_ROUTE_NAMES } from '@/router'
import checkKvkkService from '@/services/checkKvkk.service'
import { local, session, STORAGE_KEYS, SESSION_KEYS } from '@/utils/storage'

local.remove('user') // legacy key cleanup
local.remove(STORAGE_KEYS.AUTH_TOKEN)
local.remove(STORAGE_KEYS.REFRESH_TOKEN)

let logoutPromise = null
let renewTokenPromise = null

function getTrustedDeviceToken () {
  return local.get(STORAGE_KEYS.TRUSTED_DEVICE_TOKEN) || ''
}

function setTrustedDeviceToken (token) {
  if (token) {
    local.set(STORAGE_KEYS.TRUSTED_DEVICE_TOKEN, token)
  } else {
    local.remove(STORAGE_KEYS.TRUSTED_DEVICE_TOKEN)
  }
}

export const useAuthStore = defineStore({
  id: 'authStore',
  state: () => ({
    user: null,
    isLoggedIn: local.get(STORAGE_KEYS.IS_LOGIN) === 'true',
    token: null,
    refreshToken: null,
    pendingTwoFactorTicket: null,
    route: useRoute(),
    router: useRouter()
  }),
  setup () {},
  actions: {
    checkIsLoggedIn () {
      if (this.isLoggedIn) return true
      else this.router.push('/login')

      return false
    },
    signUp (formData) {
      return AuthService.signUp(formData)
    },
    login (formData) {
      const payload = { ...formData, trustedDeviceToken: getTrustedDeviceToken() }
      return AuthService.login(payload).then(
        response => {
          if (response.data !== undefined) {
            if (response.data.requires2fa) {
              this.pendingTwoFactorTicket = response.data.ticket
              return response
            }

            this.user = formData.username
            this.isLoggedIn = true
            this.token = response.data.token || null
            this.refreshToken = response.data.refresh_token || response.data.refreshToken || null
            local.set(STORAGE_KEYS.IS_LOGIN, true)
            local.remove(STORAGE_KEYS.AUTH_TOKEN)
            local.remove(STORAGE_KEYS.REFRESH_TOKEN)
          }
          return response
        }
      )
    },
    verifyTwoFactor (code, trustDevice = false) {
      const ticket = this.pendingTwoFactorTicket
      return AuthService.verify2fa({ ticket, code, trustDevice }).then(
        response => {
          if (response.data !== undefined) {
            this.isLoggedIn = true
            this.token = response.data.token || null
            this.refreshToken = response.data.refresh_token || response.data.refreshToken || null
            this.pendingTwoFactorTicket = null
            local.set(STORAGE_KEYS.IS_LOGIN, true)
            local.remove(STORAGE_KEYS.AUTH_TOKEN)
            local.remove(STORAGE_KEYS.REFRESH_TOKEN)
            if (response.data.trustedDeviceToken) {
              setTrustedDeviceToken(response.data.trustedDeviceToken)
            }
          }
          return response
        }
      )
    },
    clearPendingTwoFactor () {
      this.pendingTwoFactorTicket = null
    },
    checkKvkk () {
      const formData = {
        socialLoginClientToken: session.get(SESSION_KEYS.SOCIAL_LOGIN_ID)
      }
      return checkKvkkService.checkKvkk(formData).then(
        response => {
          if (response.status === 200) {
            if (response.data.showAgreements === false) {
              return 1
            } else {
              return 2
            }
          }
          return 0
        })
    },
    sendSocialLoginId () {
      const formData = {
        socialLoginClientToken: session.get(SESSION_KEYS.SOCIAL_LOGIN_ID),
        trustedDeviceToken: getTrustedDeviceToken()
      }
      return SocialLoginService.sendSocialLoginId(session.get(SESSION_KEYS.SOCIAL_LOGIN_NAME), formData).then(
        response => {
          if (response.status === 200 && response.data?.requires2fa) {
            // 2FA'lı admin hesabı: token henüz yok, girişi /2fa ekranı tamamlar.
            this.pendingTwoFactorTicket = response.data.ticket
            session.remove(SESSION_KEYS.SOCIAL_LOGIN_ID)
            session.remove(SESSION_KEYS.SOCIAL_LOGIN_NAME)
            return response
          }
          if (response.status === 200) {
            this.user = ''
            this.isLoggedIn = true
            this.token = response.data?.token || this.token
            this.refreshToken = response.data?.refresh_token || response.data?.refreshToken || this.refreshToken
            local.set(STORAGE_KEYS.IS_LOGIN, true)
            local.remove(STORAGE_KEYS.AUTH_TOKEN)
            local.remove(STORAGE_KEYS.REFRESH_TOKEN)
            session.remove(SESSION_KEYS.SOCIAL_LOGIN_ID)
            session.remove(SESSION_KEYS.SOCIAL_LOGIN_NAME)
          }
          return response
        })
    },
    completeSocialRegistration (formData) {
      const payload = {
        ...formData,
        socialLoginClientToken: session.get(SESSION_KEYS.SOCIAL_LOGIN_ID)
      }
      return AuthService.completeSocialRegistration(payload)
    },
    logout (options = {}) {
      if (logoutPromise) {
        return logoutPromise
      }

      const { skipApi = false } = options

      if (!skipApi && !this.isLoggedIn && !this.token) {
        return Promise.resolve()
      }

      logoutPromise = (async () => {
        if (!skipApi) {
          await AuthService.logout().catch(() => {})
        }

        this.user = null
        this.isLoggedIn = false
        this.token = null
        this.refreshToken = null
        local.remove(STORAGE_KEYS.AUTH_TOKEN)
        local.remove(STORAGE_KEYS.REFRESH_TOKEN)
        local.remove(STORAGE_KEYS.IS_SOCIAL_CATEGORIES_SELECTED)
        session.remove(SESSION_KEYS.SOCIAL_LOGIN_ID)
        session.remove(SESSION_KEYS.SOCIAL_LOGIN_NAME)
        local.set(STORAGE_KEYS.IS_LOGIN, false)

        const currentRouteName = this.router?.currentRoute?.value?.name
        if (!PUBLIC_ROUTE_NAMES.has(currentRouteName)) {
          await this.router.push('/login').catch(() => {})
        }
      })().finally(() => {
        logoutPromise = null
      })

      return logoutPromise
    },
    getToken () {
      return this.token
    },
    getRefreshToken () {
      return this.refreshToken
    },
    renewToken () {
      if (renewTokenPromise) {
        return renewTokenPromise
      }

      // Keep the old token live until the new one arrives — nulling it here
      // makes any request built during this window go out with no
      // Authorization header at all ("JWT Token not found" instead of a
      // normal expired-token 401 that the retry flow already handles).
      renewTokenPromise = AuthService.renewToken().then(
        response => {
          if (response.data !== undefined) {
            this.isLoggedIn = true
            this.token = response.data.token || null
            this.refreshToken = response.data.refresh_token || response.data.refreshToken || this.refreshToken
            local.set(STORAGE_KEYS.IS_LOGIN, true)
            local.remove(STORAGE_KEYS.AUTH_TOKEN)
            local.remove(STORAGE_KEYS.REFRESH_TOKEN)
          }
          return response
        }
      ).finally(() => {
        renewTokenPromise = null
      })

      return renewTokenPromise
    },
    activate () {
      // Aktivasyon işlemi için
      const token = this.route.params.token
      return AuthService.activate(token)
    },
    activationSignUp (formData) {
      // Kayıt+Aktivasyon işlemi için
      const token = this.route.params.token
      formData.invitation_token = token
      return AuthService.signUp(formData)
    },
    updatePassword (formData) {
      return AuthService.updatePassword(formData)
    },
    forgotPassword (formData) {
      return AuthService.forgotPassword(formData)
    },
    resetPassword (formData) {
      return AuthService.resetPassword(formData)
    },
    activateUser (token) {
      const formData = {
        token: token
      }
      return AuthService.activateUser(formData)
    },
    resendActivation (email, turnstileToken) {
      return AuthService.resendActivation({ email, turnstileToken })
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
